import React, { useEffect, useMemo, useState } from "react";
import { Navigation } from "../components/navigation";
import { Footer } from "../components/footer";
import "./donors/donors.css";

const tabs = [
  { key: "All", label: "အားလုံး" },
  { key: "General_Donor", label: "အဆောက်အဦအလှူရှင်များ" },
  { key: "Meal_Donor", label: "ဆွမ်းအဟာရ အလှူရှင်များ" },
  { key: "Drink_Donor", label: "ညနေခင်းဖျော်ရည် အလှူရှင်များ" },
];

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "") ?? "";
const buildApiUrl = (endpoint) => (API_BASE_URL ? `${API_BASE_URL}${endpoint}` : endpoint);

const donorTypeLabels = {
  Meal_Donor: "ဆွမ်းအဟာရ အလှူရှင်များ",
  Drink_Donor: "ညနေခင်းဖျော်ရည် အလှူရှင်များ",
  General_Donor: "အဆောက်အဦအလှူရှင်များ",
};

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
};

const DonorsPage = () => {
  const [searchValue, setSearchValue] = useState("");
  const [activeType, setActiveType] = useState("All");
  const [donors, setDonors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    const loadDonors = async () => {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const response = await fetch(buildApiUrl("/donors"), {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`Failed to load donors (${response.status})`);
        }
        const data = await response.json();
        const normalized = Array.isArray(data) ? data : data?.data ?? [];
        setDonors(normalized);
      } catch (error) {
        if (error.name === "AbortError") return;
        setErrorMessage("Unable to load donors right now. Please try again.");
        setDonors([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadDonors();

    return () => controller.abort();
  }, []);

  const filteredDonors = useMemo(() => {
    if (!searchValue) return donors;
    const normalized = searchValue.toLowerCase();
    return donors.filter((donor) => {
      const name = donor.donor_name?.toLowerCase() ?? "";
      const contact = donor.contact_number?.toLowerCase() ?? "";
      const request = donor.special_request?.toLowerCase() ?? "";
      const type = donor.donor_type?.toLowerCase() ?? "";
      return (
        name.includes(normalized) ||
        contact.includes(normalized) ||
        request.includes(normalized) ||
        type.includes(normalized)
      );
    });
  }, [donors, searchValue]);

  const visibleDonors = useMemo(() => {
    if (activeType === "All") return filteredDonors;
    return filteredDonors.filter((donor) => donor.donor_type === activeType);
  }, [activeType, filteredDonors]);

  return (
    <>
      <Navigation />
      <div className="donors-page">
        <section className="donor-hero">
          <h2 className="donor-hero__title">အလှူရှင်များ</h2>
          <p className="donor-hero__description">
            ကျေးဇူးရှင် ဒါယကာ၊ ဒါယိကာမကြီးများ၏ ရက်ရောလှသော ပစ္စယာနုဂ္ဂဟကို ခံယူကာ၊ ဤ သာသနာ့ဝန်ထမ်း အရေးကိစ္စအဝဝကို အောင်မြင်စွာ ဆောင်ရွက်နိုင်ပါသည်။ ဤ ကျေးဇူးတရားကို "ဥဒ္ဒိဿ" တည်းဟူသော ကျေးဇူးသိမ်းဆည်းမှုဖြင့် ချီးမြှင့်လိုက်ပါသည်။
          </p>
          <div className="donor-tabs">
            {tabs.map((tab) => (
              <button
                type="button"
                key={tab.key}
                className={`donor-tabs__item ${
                  activeType === tab.key ? "is-active" : ""
                }`}
                onClick={() => setActiveType(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </section>

        <div className="donor-search">
          <input
            type="search"
            placeholder="Search by name, contact, request, or type..."
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
          />
        </div>

        <section className="donor-grid">
          {isLoading && <div className="donor-status">Loading donors...</div>}
          {!isLoading && errorMessage && (
            <div className="donor-status is-error">{errorMessage}</div>
          )}
          {!isLoading && !errorMessage && visibleDonors.length === 0 && (
            <div className="donor-status">No donors found.</div>
          )}
          {!isLoading &&
            !errorMessage &&
            visibleDonors.map((donor) => {
              const donorType =
                donorTypeLabels[donor.donor_type] ?? donor.donor_type ?? "Donor";
              return (
                <article
                  key={`${donor.donor_name}-${donor.contact_number ?? "unknown"}`}
                  className="donor-card"
                >
                  <div className="donor-card__header">
                    <div className="donor-avatar" aria-hidden="true">
                      {getInitials(donor.donor_name)}
                    </div>
                    <div className="donor-card__title">
                      <h2>{donor.donor_name ?? "Unknown Donor"}</h2>
                      <span className="donor-type">{donorType}</span>
                    </div>
                  </div>
                  <div className="donor-card__body">
                    {donor.contact_number && (
                      <div className="donor-meta">
                        <span className="donor-meta__label">Contact</span>
                        <span className="donor-meta__value">
                          {donor.contact_number}
                        </span>
                      </div>
                    )}
                    {donor.special_request && (
                      <div className="donor-meta">
                        <span className="donor-meta__label">Request</span>
                        <span className="donor-meta__value">
                          {donor.special_request}
                        </span>
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
        </section>
      </div>
      <Footer />
    </>
  );
};

export default DonorsPage;
